import { memo, MouseEvent, useCallback } from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useSoloOrario } from '../../hooks/useSoloOrario';
import type { CalendarioId } from '../../utility_objects/preferenzeCalendario';
import { RigaVisualizzazione } from '../Visualizzazione/RigaVisualizzazione';

/** Viste senza griglia delle ore: il selettore resta visibile ma disabilitato. */
type VistaSenzaOre = 'Month' | 'Agenda';

/** Voci del selettore: «Prenotabili» è la funzione «Solo orario» accesa. */
type Ore = 'tutte' | 'prenotabili';

const SPIEGAZIONE =
  "Prenotabili: nasconde le ore prima dell'apertura e dopo la chiusura; le ore con appuntamenti restano visibili.";
const SPIEGAZIONE_SENZA_ORE: Record<VistaSenzaOre, string> = {
  Month: 'Nella vista Mese non ci sono ore da nascondere.',
  Agenda: 'Nella vista Agenda non ci sono ore da nascondere.',
};

const getVistaSenzaOre = (view: string): VistaSenzaOre | null =>
  view === 'Month' || view === 'Agenda' ? view : null;

interface SoloOrarioToggleProps {
  /** Calendario di cui si sceglie: ognuno ha la sua scelta. */
  calendario: CalendarioId;
  /** Vista del calendario: nel Mese e nell'Agenda il selettore è disabilitato. */
  view: string;
}

/**
 * Riga «Ore» del riquadro Visualizzazione: «Tutte» (le ore di sempre del
 * calendario) o «Prenotabili» (solo le ore dall'apertura alla chiusura, più
 * quelle con appuntamenti). Come il gruppo delle viste c'è sempre una voce
 * selezionata: il clic su quella già scelta non fa nulla. Nel Mese e
 * nell'Agenda resta com'è ma disabilitato, perché lì non ci sono ore. Scelta
 * del calendario (useSoloOrario): cambiarla in un calendario non tocca gli
 * altri.
 */
export const SoloOrarioToggle = memo(function SoloOrarioToggle({
  calendario,
  view,
}: SoloOrarioToggleProps): JSX.Element {
  const { soloOrario, imposta } = useSoloOrario(calendario);

  // null = clic sulla voce già selezionata: resta quella
  const onChangeOre = useCallback(
    (_evento: MouseEvent<HTMLElement>, valore: Ore | null) => {
      if (valore !== null) imposta(valore === 'prenotabili');
    },
    [imposta]
  );

  const vistaSenzaOre = getVistaSenzaOre(view);
  const spiegazione =
    vistaSenzaOre === null ? SPIEGAZIONE : SPIEGAZIONE_SENZA_ORE[vistaSenzaOre];

  return (
    <RigaVisualizzazione etichetta="Ore" spiegazione={spiegazione}>
      {(aria) => (
        <ToggleButtonGroup
          exclusive
          fullWidth
          value={soloOrario ? 'prenotabili' : 'tutte'}
          onChange={onChangeOre}
          {...aria}
          data-testid="calendar-ore-toggle"
        >
          <ToggleButton
            size="small"
            value="tutte"
            disabled={vistaSenzaOre !== null}
            data-testid="calendar-ore-tutte"
          >
            Tutte
          </ToggleButton>
          <ToggleButton
            size="small"
            value="prenotabili"
            disabled={vistaSenzaOre !== null}
            data-testid="calendar-ore-prenotabili"
          >
            Prenotabili
          </ToggleButton>
        </ToggleButtonGroup>
      )}
    </RigaVisualizzazione>
  );
});
